<template>
  <div class="shirt-mockup" :class="`shirt-mockup--${size}`">
    <div class="shirt-mockup__item" :style="{ backgroundColor: selectedColor }">
      <img :src="imgSrc" alt="Shirt Design" class="shirt-mockup__item-image" :draggable="false" />
      <div class="shirt-mockup__bounds-box" :style="boundsStyle">
        <img
          v-if="artworkUrl && !isCustom"
          :src="artworkUrl"
          alt="Artwork"
          class="shirt-mockup__artwork"
          :draggable="false"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";

const PRESET_CONFIG = {
  "full-front": { bounds: { x: 40, y: 40, width: 35, height: 48 } },
  "full-back": { bounds: { x: 43, y: 39, width: 35, height: 57 } },
  "left-chest": { bounds: { x: 49, y: 30, width: 20, height: 20 } },
  sleeve: { bounds: { x: 25, y: 33, width: 15, height: 14 } },
  "back-collar": { bounds: { x: 43, y: 20, width: 20, height: 20 } }
};

const props = defineProps({
  artworkUrl: {
    type: String,
    default: "https://jiffy.imgix.net/gen/size-mockup/placeholder.png?ixlib=rb-0.3.5&h=100&dpr=2"
  },
  selectedColor: {
    type: String,
    default: "#E1E1E1"
  },
  preset: {
    type: String,
    default: "full-front",
    validator: value =>
      ["custom", "full-front", "full-back", "left-chest", "sleeve", "back-collar"].includes(value)
  },
  size: {
    type: String,
    default: "small",
    validator: value => ["small", "medium", "large"].includes(value)
  }
});

const isBack = computed(() => props.preset === "full-back" || props.preset === "back-collar");
const isSleeve = computed(() => props.preset === "sleeve");
const isCustom = computed(() => props.preset === "custom");
const multiplier = computed(() => {
  if (props.size === "large") return 2.2;
  if (props.size === "medium") return 1.88;
  return 1;
});

const imgSrc = computed(() => {
  if (isBack.value)
    return "https://jiffy.imgix.net/gen/size-mockup/back.png?ixlib=rb-0.3.5&h=100&dpr=2";
  if (isSleeve.value)
    return "https://jiffy.imgix.net/gen/size-mockup/side.png?ixlib=rb-0.3.5&h=100&dpr=2";
  return "https://jiffy.imgix.net/gen/size-mockup/front.png?ixlib=rb-0.3.5&h=100&dpr=2";
});

const presetBounds = PRESET_CONFIG[props.preset]?.bounds;

const boundsStyle = computed(() => {
  if (!presetBounds) return {};

  const topLeftX = presetBounds.x - presetBounds.width / 2;
  const topLeftY = presetBounds.y - presetBounds.height / 2;

  return {
    top: `${topLeftY * multiplier.value}px`,
    left: `${topLeftX * multiplier.value}px`,
    width: `${presetBounds.width * multiplier.value}px`,
    height: `${presetBounds.height * multiplier.value}px`
  };
});
</script>

<style scoped lang="scss">
.shirt-mockup {
  $this: &;

  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  &--small {
    #{$this}__item-image {
      height: 100px;
    }
  }

  &--medium {
    #{$this}__item-image {
      height: 188px;
    }
  }

  &--large {
    #{$this}__item-image {
      height: 220px;
    }
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: center;
    width: fit-content;
    position: relative;
    overflow: hidden;
    background-color: #fff;
  }

  &__item-image {
    height: 100px;
    width: auto;
    object-fit: contain;
    position: relative;
    z-index: 1;
  }

  &__bounds-box {
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    pointer-events: none;
    z-index: 1;
  }

  &__artwork {
    pointer-events: none;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
}
</style>
